import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Plus, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
}).format(amount || 0);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function VendorPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    vendor_id: '',
    po_id: 'none',
    amount: '',
    payment_method: 'cash',
    notes: '',
    payment_date: ''
  });

  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const [payRes, vendorRes, poRes] = await Promise.all([
        adminApi.getVendorPayments(),
        adminApi.getVendors(),
        adminApi.getPurchaseOrders()
      ]);
      setPayments(payRes.data || []);
      setVendors(vendorRes.data || []);
      setPurchaseOrders(poRes.data || []);
    } catch (error) {
      toast.error('Failed to load vendor payments');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      vendor_id: '',
      po_id: 'none',
      amount: '',
      payment_method: 'cash',
      notes: '',
      payment_date: ''
    });
  };

  const handleSave = async () => {
    if (!form.vendor_id) {
      toast.error('Select vendor');
      return;
    }
    if (!form.amount) {
      toast.error('Enter amount');
      return;
    }

    setSaving(true);
    try {
      await adminApi.createVendorPayment({
        vendor_id: form.vendor_id,
        po_id: form.po_id === 'none' ? undefined : form.po_id,
        amount: parseFloat(form.amount),
        payment_method: form.payment_method,
        notes: form.notes || undefined,
        payment_date: form.payment_date || undefined
      });
      toast.success('Payment recorded');
      setShowModal(false);
      resetForm();
      fetchInitial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    } finally {
      setSaving(false);
    }
  };

  const vendorOptions = useMemo(() => vendors.filter((v) => v.is_active), [vendors]);
  const poOptions = useMemo(() => purchaseOrders.filter((p) => p.status !== 'cancelled'), [purchaseOrders]);

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('vendor-payments');
      downloadBlob(res.data, 'vendor-payments.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-payments-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Vendor Payments</h1>
        <p className="text-[#A8A8A8] mt-1">Record vendor payments and vouchers</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#2D2420]">Payments</h2>
              <p className="text-sm text-[#A8A8A8]">Create payment vouchers</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleDownloadExcel}>
                Download Excel
              </Button>
              <Button onClick={() => { resetForm(); setShowModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : payments.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
              <CreditCard className="w-6 h-6 mr-2" />
              No vendor payments yet
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Voucher</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Vendor</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">PO</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Method</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{payment.voucher_no}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{payment.vendor_name}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{payment.po_id ? payment.po_id : '-'}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{formatDate(payment.payment_date)}</td>
                      <td className="py-3 px-4 text-right text-[#2D2420]">{formatCurrency(payment.amount)}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{payment.payment_method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Record Vendor Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor</Label>
              <Select value={form.vendor_id} onValueChange={(value) => setForm((prev) => ({ ...prev, vendor_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendorOptions.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Purchase Order (optional)</Label>
              <Select value={form.po_id} onValueChange={(value) => setForm((prev) => ({ ...prev, po_id: value }))}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select PO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No PO</SelectItem>
                  {poOptions.map((po) => (
                    <SelectItem key={po.id} value={po.id}>{po.po_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(value) => setForm((prev) => ({ ...prev, payment_method: value }))}>
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm((prev) => ({ ...prev, payment_date: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
